// 機房（plan SPEC #kinio-station-seat-site-share，Eddy 2026-10-20 選單）：家戶 owner / admin 經 station.use 查機房管理站的
// 網路、伺服器、NAS 狀態。只有查詢，dev / prod、各品牌都一樣（不是 dev 專用的工位頁）。
//   GET  /v1/homes/{home}/things/{aid}/station/tools        → {tools: [{name, summary, params}]}（沒回報機房資源 = 空）
//   POST /v1/homes/{home}/things/{aid}/station/runs         {tool, params?} → 202 {run_id, state}
//   GET  /v1/homes/{home}/things/{aid}/station/runs/{run}   → {run_id, tool, state, started_at, ended_at?, result?, error?}
// 同一人同一台 5 秒最多送 1 次執行（429 RATE_LIMITED，detail.retry_after_seconds）。
import { kinioFetch, planeBase, isKinioError } from '@kinio/web-sdk';

export interface SiteTool {
  name: string;
  summary: string;
  params?: unknown;
}

export interface SiteRun {
  run_id: string;
  tool?: string;
  state: string;
  started_at?: string;
  ended_at?: string;
  result?: unknown;
  error?: { code?: string; message?: string } | null;
}

/** 一台我能查的 Kinio Station（我是它所在家戶的 owner / admin）。 */
export interface SiteStation {
  api: string;
  homeId: string;
  homeName: string;
  aid: string;
  name: string;
  online: boolean;
}

const STATION_MODEL = /^kinio-station-/i;

/** 「我的設備」裡、我是家戶 owner / admin 的 Kinio Station（平台另外判斷 station.use；這裡只決定要不要去問）。 */
export function siteStations(
  homes: { home_id: string; display_name?: string; role?: string }[],
  devices: { device_ref: string; display_name: string; model?: string; home_id: string; api: string; online: boolean }[],
): SiteStation[] {
  const admin = new Map(homes.filter((h) => h.role === 'owner' || h.role === 'admin').map((h) => [h.home_id, h.display_name ?? h.home_id]));
  return devices
    .filter((d) => STATION_MODEL.test(d.model ?? '') && admin.has(d.home_id))
    .map((d) => ({ api: d.api, homeId: d.home_id, homeName: admin.get(d.home_id) ?? '', aid: d.device_ref, name: d.display_name || d.device_ref, online: d.online }))
    .sort((a, b) => a.homeName.localeCompare(b.homeName) || a.name.localeCompare(b.name));
}

function base(s: Pick<SiteStation, 'api' | 'homeId' | 'aid'>): string {
  return `${planeBase(s.api)}/v1/homes/${encodeURIComponent(s.homeId)}/things/${encodeURIComponent(s.aid)}/station`;
}

export const siteApi = {
  tools: async (s: SiteStation): Promise<SiteTool[]> => (await kinioFetch<{ tools: SiteTool[] }>(`${base(s)}/tools`))?.tools ?? [],
  run: (s: SiteStation, tool: string, params?: Record<string, unknown>) =>
    kinioFetch<{ run_id: string; state: string }>(`${base(s)}/runs`, { method: 'POST', body: { tool, params: params ?? {} } }),
  get: (s: SiteStation, runId: string) => kinioFetch<SiteRun>(`${base(s)}/runs/${encodeURIComponent(runId)}`),
};

/** 還在跑的狀態（tools 的 queued / running）。 */
export function runActive(state: string | undefined): boolean {
  return state === 'queued' || state === 'running' || state === 'starting';
}

/** 429 的等待秒數（沒有 = 0）。 */
export function retryAfter(e: unknown): number {
  if (!isKinioError(e) || e.code !== 'RATE_LIMITED') return 0;
  const s = e.retryAfterS ?? Number(e.detail.retry_after_seconds);
  return Number.isFinite(s) && s > 0 ? s : 5;
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * 跑一個工具並等結果：被限流就照 retry_after 等了再送（最多再送 3 次）；之後每秒查一次（查結果被限流也照 retry_after 等），
 * 最多 timeoutS 秒；cancelled() 為真（換了分頁）就不再送、不再查。
 * 錯誤丟 KinioError（呼叫端顯示碼）。
 */
export async function runAndWait(
  s: SiteStation,
  tool: string,
  opts: { params?: Record<string, unknown>; timeoutS?: number; onWait?: (sec: number) => void; cancelled?: () => boolean } = {},
): Promise<SiteRun> {
  const cancelled = opts.cancelled ?? (() => false);
  let started: { run_id: string; state: string } | null = null;
  for (let attempt = 0; !started; attempt++) {
    try {
      started = await siteApi.run(s, tool, opts.params);
    } catch (e) {
      const wait = retryAfter(e);
      if (!wait || attempt >= 3 || cancelled()) throw e;
      opts.onWait?.(wait);
      await sleep(wait * 1000);
      if (cancelled()) throw e;
    }
  }
  const deadline = Date.now() + (opts.timeoutS ?? 90) * 1000;
  let run: SiteRun = { run_id: started.run_id, state: started.state };
  while (runActive(run.state) && Date.now() < deadline && !cancelled()) {
    await sleep(1000);
    try {
      run = await siteApi.get(s, started.run_id);
    } catch (e) {
      const wait = retryAfter(e); // 查結果被限流就照 retry_after 等，不當失敗
      if (!wait) throw e;
      await sleep(wait * 1000);
    }
  }
  return run;
}

/** 機房頁的分頁：每頁的工具依序（第一個 = 打開時跑的那個）。 */
export const SITE_TABS: { key: 'overview' | 'router' | 'ap' | 'servers' | 'nas'; tools: string[] }[] = [
  { key: 'overview', tools: ['site.status'] },
  { key: 'router', tools: ['net.devices', 'net.wan', 'net.wifi', 'net.traffic', 'net.dhcp.list', 'net.forward.list', 'net.logs'] },
  // Wi-Fi 基地台（Kinio AP；SPEC #kinio-station-seat-site「怎麼連設備」、ST-SHARE-08）：一站一台，工具自己找那台、不帶 dut
  { key: 'ap', tools: ['ap.status', 'ap.clients', 'ap.wifi'] },
  { key: 'servers', tools: ['srv.health'] },
  { key: 'nas', tools: ['nas.health'] },
];

/** 伺服器 / NAS 分頁對應的資源類型（site.status 的 resources[].type；有多台時 srv.health / nas.health 要帶 dut）。 */
export const TAB_RESOURCE: Partial<Record<(typeof SITE_TABS)[number]['key'], string>> = { servers: 'server', nas: 'nas' };

/** 這台有的分頁（工具清單裡至少有一個）。 */
export function availableTabs(tools: SiteTool[]): typeof SITE_TABS {
  const have = new Set(tools.map((t) => t.name));
  return SITE_TABS.map((tab) => ({ ...tab, tools: tab.tools.filter((n) => have.has(n)) })).filter((tab) => tab.tools.length > 0);
}

/** 結果的呈現：物件陣列 → 表格，其他 → 鍵值。 */
export type ResultBlock =
  | { kind: 'table'; title: string; columns: string[]; rows: Record<string, unknown>[] }
  | { kind: 'fields'; title: string; fields: [string, unknown][] }
  | { kind: 'lines'; title: string; lines: string[] };

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** 把工具結果拆成可畫的區塊（每層的純量一個鍵值區塊；物件陣列、{columns, rows} 各一張表；字串陣列（日誌）一段文字；子物件遞迴）。 */
export function resultBlocks(result: unknown, title = ''): ResultBlock[] {
  if (Array.isArray(result)) {
    if (result.length > 0 && result.every(isRecord)) {
      const cols: string[] = [];
      for (const r of result) for (const k of Object.keys(r)) if (!cols.includes(k)) cols.push(k);
      return [{ kind: 'table', title, columns: cols, rows: result }];
    }
    return [{ kind: 'fields', title, fields: [[title || 'value', result]] }];
  }
  if (!isRecord(result)) return [{ kind: 'fields', title, fields: [[title || 'value', result]] }];
  const scalars: [string, unknown][] = [];
  const nested: ResultBlock[] = [];
  // 機房工具的精簡表格：{columns: [欄名…], rows: [[值…]…]}（stationtools 的 net.* / srv.*）→ 一張表，其餘欄位照常
  const cols = result.columns;
  const rows = result.rows;
  const compact = Array.isArray(cols) && cols.every((c) => typeof c === 'string') && Array.isArray(rows) && rows.every(Array.isArray);
  if (compact) {
    const names = cols as string[];
    nested.push({ kind: 'table', title, columns: names, rows: (rows as unknown[][]).map((r) => Object.fromEntries(names.map((c, i) => [c, r[i]]))) });
  }
  for (const [k, v] of Object.entries(result)) {
    if (compact && (k === 'columns' || k === 'rows')) continue;
    const name = title ? `${title}.${k}` : k;
    if (Array.isArray(v) && v.length > 0 && v.every(isRecord)) nested.push(...resultBlocks(v, name));
    else if (Array.isArray(v) && v.length > 0 && v.every((x) => typeof x === 'string')) nested.push({ kind: 'lines', title: name, lines: v as string[] });
    else if (isRecord(v)) nested.push(...resultBlocks(v, name));
    else scalars.push([k, v]);
  }
  return [...(scalars.length ? [{ kind: 'fields' as const, title, fields: scalars }] : []), ...nested];
}

/** 表格 / 鍵值裡一格的文字。 */
export function cellText(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'boolean') return v ? '✓' : '✗';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}
